
import api from '../api';
import { useToast } from '../context/ToastContext';

/**
 * Hook to manage user actions (add, edit, delete, email).
 * @param {Function} onSuccessCallback - Callback to run after a successful action (e.g., refresh list)
 * @returns {Object} Action handlers and state
 */
export const useUserActions = (onSuccessCallback) => {
  const { success: toastSuccess, error: toastError } = useToast();

  const handleCreateUser = async (formData, closeModal) => {
    try {
      const response = await api.post('/api/auth/register', {
        ...formData,
        role: 'user' // Default role
      });
      if (response.data.success) {
        toastSuccess('User created successfully');
        closeModal();
        if (onSuccessCallback) onSuccessCallback();
        return true;
      }
    } catch (error) {
      console.error('Create user error:', error);
      toastError(error.response?.data?.message || 'Failed to create user');
      return false;
    }
  };

  const handleUpdateUser = async (userId, formData, closeModal) => {
    try {
      const response = await api.put(`/api/admin/users/${userId}`, formData);
      if (response.data.success) {
        toastSuccess('User updated successfully');
        closeModal();
        if (onSuccessCallback) onSuccessCallback();
        return true;
      }
    } catch (error) {
      console.error('Update user error:', error);
      toastError(error.response?.data?.message || 'Failed to update user');
      return false;
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await api.delete(`/api/admin/users/${userId}`);
      if (response.data.success) {
        toastSuccess('User deleted successfully');
        if (onSuccessCallback) onSuccessCallback();
      }
    } catch (error) {
      console.error('Delete user error:', error);
      toastError('Failed to delete user');
    }
  };

  const handleSendEmail = async (userId, formData, closeModal) => {
    try {
      // Assuming endpoint exists for sending email
      const response = await api.post(`/api/admin/users/${userId}/email`, formData);
      if (response.data.success) {
        toastSuccess('Email sent successfully');
        if (closeModal) closeModal();
        return true;
      }
    } catch (error) {
      console.error('Send email error:', error);
      toastError('Failed to send email');
      return false;
    }
  };

  return {
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleSendEmail
  };
};
